import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'fb:chunk-reload-attempted';

/**
 * `React.lazy` that recovers from a stale module graph.
 *
 * Vite gives every chunk a content hash. A tab left open across an api rebuild
 * still holds the pre-rebuild graph, so navigating to a lazy route requests a
 * hash that no longer exists and the import rejects — the user lands on
 * "Failed to fetch dynamically imported module" with no way forward.
 *
 * Reloading fetches the current index.html and its chunk names, which resolves
 * it. The sessionStorage flag bounds this to a single attempt so a genuinely
 * broken chunk surfaces as an error instead of an infinite reload loop; a
 * successful load clears it so a later deploy can recover the same way.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React's
// own `lazy<T extends ComponentType<any>>` signature; narrowing it here makes
// the wrapper unassignable to lazy().
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return module;
    } catch (error) {
      if (sessionStorage.getItem(RELOAD_KEY)) throw error;
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
      // The reload takes over; never resolve so no error screen flashes first.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
